import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useTablePagination } from "@components/common/hooks/useTablePagination";
import { fetchMarketsCoinsFromCoinranking } from "../../../../../../services/coinrankingMarketCoins";

export const MARKETS_TABLE_CATEGORY = {
  all: "all",
  favorites: "favorites",
  newListings: "newListings",
};

const FAVORITES_STORAGE_KEY = "marketFavorites";

/** Оновлення таблиці з API без перезавантаження сторінки (~2 хв). */
const MARKETS_TABLE_REFRESH_MS = 120_000;

const loadFavoriteIds = () => {
  if (typeof window === "undefined") {
    return new Set();
  }

  try {
    const stored = window.localStorage.getItem(FAVORITES_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return new Set(parsed);
      }
    }
  } catch (error) {
    console.warn("Failed to parse favorites from storage", error);
  }

  return new Set();
};

export const useMarketsTableData = (
  defaultCategory = MARKETS_TABLE_CATEGORY.all,
) => {
  const [coins, setCoins] = useState([]);
  const [category, setCategory] = useState(defaultCategory);
  const [segment, setSegment] = useState("all");
  const [favorites, setFavorites] = useState(() => loadFavoriteIds());
  const [sortBy, setSortBy] = useState(null);
  const [sortDirection, setSortDirection] = useState("desc");
  const [vsCurrency, setVsCurrency] = useState("USD");
  const [search, setSearch] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const searchInputRef = useRef(null);
  const debounceTimerRef = useRef(null);

  const itemsPerPage = 10;

  useEffect(() => {
    let cancelled = false;

    const fetchCoins = async () => {
      setIsLoading(true);

      try {
        const data = await fetchMarketsCoinsFromCoinranking();
        if (cancelled) return;
        setCoins(data);
      } catch (error) {
        console.error("Markets table Coinranking fetch failed:", error);
        if (!cancelled) {
          setCoins([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchCoins();
    return () => {
      cancelled = true;
    };
  }, [vsCurrency]);

  useEffect(() => {
    if (typeof window === "undefined" || isLoading) {
      return undefined;
    }

    const refresh = async () => {
      if (typeof document !== "undefined" && document.hidden) {
        return;
      }
      try {
        const data = await fetchMarketsCoinsFromCoinranking();
        setCoins(data);
      } catch (error) {
        console.error("Markets table Coinranking refresh failed:", error);
      }
    };

    const id = window.setInterval(refresh, MARKETS_TABLE_REFRESH_MS);
    return () => clearInterval(id);
  }, [isLoading, vsCurrency]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    window.localStorage.setItem(
      FAVORITES_STORAGE_KEY,
      JSON.stringify(Array.from(favorites)),
    );
  }, [favorites]);

  const filteredCoins = useMemo(() => {
    const normalizedQuery = search.trim().toLowerCase();

    const matchesQuery = (coin) =>
      coin.name.toLowerCase().includes(normalizedQuery) ||
      coin.symbol.toLowerCase().includes(normalizedQuery);

    let baseList = coins;

    if (category === MARKETS_TABLE_CATEGORY.favorites) {
      baseList = coins.filter((coin) => favorites.has(coin.id));
    } else if (category === MARKETS_TABLE_CATEGORY.newListings) {
      baseList = coins.filter((coin) => {
        return coin.price_change_percentage_24h > 0;
      });
    } else {
      baseList = coins;
    }

    const bySegment = baseList.filter((coin) => {
      const slugId = String(coin.coinGeckoId || coin.id || "").toLowerCase();
      const name = (coin.name || "").toLowerCase();
      const symbol = (coin.symbol || "").toLowerCase();

      if (segment === "all") return true;

      if (segment === "solana") {
        return (
          slugId.includes("solana") ||
          symbol === "sol" ||
          name.includes("solana")
        );
      }

      if (segment === "eco") {
        return name.includes("eco") || slugId.includes("eco");
      }

      if (segment === "ai") {
        return (
          name.includes("ai") || slugId.includes("ai") || symbol.includes("ai")
        );
      }

      if (segment === "meme") {
        const memeHints = ["doge", "shib", "pepe", "meme", "inu", "floki"];
        return memeHints.some(
          (hint) => slugId.includes(hint) || name.includes(hint),
        );
      }

      if (segment === "gamefi") {
        const gameHints = [
          "game",
          "arena",
          "guild",
          "metaverse",
          "hero",
          "quest",
        ];
        return gameHints.some(
          (hint) => slugId.includes(hint) || name.includes(hint),
        );
      }

      if (segment === "bsc") {
        return slugId.includes("bsc") || name.includes("bsc");
      }

      if (segment === "bitcoin") {
        return (
          symbol === "btc" ||
          name.includes("bitcoin") ||
          slugId.includes("bitcoin")
        );
      }

      if (segment === "ethereum") {
        return (
          symbol === "eth" ||
          name.includes("ethereum") ||
          slugId.includes("ethereum")
        );
      }

      return true;
    });

    const searched = normalizedQuery
      ? bySegment.filter(matchesQuery)
      : bySegment;

    if (!sortBy) {
      return searched;
    }

    const direction = sortDirection === "asc" ? 1 : -1;

    const sorted = [...searched].sort((a, b) => {
      if (sortBy === "pair") {
        const aSymbol = (a.symbol || "").toLowerCase();
        const bSymbol = (b.symbol || "").toLowerCase();
        return direction * aSymbol.localeCompare(bSymbol);
      }

      let aValue = 0;
      let bValue = 0;

      if (sortBy === "price") {
        aValue = a.current_price;
        bValue = b.current_price;
      } else if (sortBy === "change") {
        aValue = a.price_change_percentage_24h;
        bValue = b.price_change_percentage_24h;
      }

      if (aValue === undefined || aValue === null) aValue = 0;
      if (bValue === undefined || bValue === null) bValue = 0;

      if (aValue === bValue) return 0;
      return aValue > bValue ? direction : -direction;
    });

    return sorted;
  }, [coins, favorites, category, segment, search, sortBy, sortDirection]);

  const {
    currentPage,
    totalPages,
    paginatedData: paginatedCoins,
    paginationRange,
    showPagination,
    handlePageChange,
    handleEllipsisClick,
    resetPage,
  } = useTablePagination({
    data: filteredCoins,
    itemsPerPage,
    siblingCount: 0,
  });

  const handleCategoryChange = useCallback(
    (newCategory) => {
      setCategory(newCategory);
      resetPage();
    },
    [resetPage],
  );

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setSearch(inputValue);
      resetPage();
    }, 500);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [inputValue, resetPage]);

  const handleSearchChange = useCallback((event) => {
    setInputValue(event.target.value);
  }, []);

  const handleSearchToggle = useCallback(() => {
    setIsSearchOpen((prev) => {
      const next = !prev;
      if (!prev) {
        requestAnimationFrame(() => searchInputRef.current?.focus());
      }
      return next;
    });
  }, []);

  const handleSearchBlur = useCallback(() => {
    if (!inputValue) {
      setIsSearchOpen(false);
    }
  }, [inputValue]);

  const handleSearchClear = useCallback(() => {
    setInputValue("");
    setSearch("");
    setIsSearchOpen(false);
  }, []);

  const handleToggleFavorite = useCallback((coinId) => {
    setFavorites((prev) => {
      const next = new Set(prev);

      if (next.has(coinId)) {
        next.delete(coinId);
      } else {
        next.add(coinId);
      }
      return next;
    });
  }, []);

  const handleSortChange = useCallback(
    (columnKey) => {
      resetPage();

      if (sortBy !== columnKey) {
        setSortBy(columnKey);
        setSortDirection("desc");
        return;
      }

      if (sortDirection === "desc") {
        setSortDirection("asc");
        return;
      }

      setSortBy(null);
      setSortDirection("desc");
    },
    [sortBy, sortDirection, resetPage],
  );

  const handleSegmentChange = useCallback(
    (value) => {
      setSegment(value);
      resetPage();
    },
    [resetPage],
  );

  const handleVsCurrencyChange = useCallback(
    (value) => {
      setVsCurrency(value);
      resetPage();
    },
    [resetPage],
  );

  return {
    category,
    search: inputValue,
    isSearchOpen,
    searchInputRef,
    favorites,
    coins,
    paginatedCoins,
    showPagination,
    paginationRange,
    currentPage,
    totalPages,
    sortBy,
    sortDirection,
    vsCurrency,
    segment,
    isLoading,
    handleCategoryChange,
    handleSearchChange,
    handleSearchToggle,
    handleSearchBlur,
    handleSearchClear,
    handleToggleFavorite,
    handlePageChange,
    handleEllipsisClick,
    handleSortChange,
    handleSegmentChange,
    handleVsCurrencyChange,
  };
};
